import React, { useEffect, useRef, useState, Suspense } from "react";
import { css } from "@emotion/css";
import type {
  WorkLane as WorkLaneType,
  UiInteractionSpec,
} from "../../types/state";
import type { UiInteractionValue } from "../../types/events";
import type { EmissionReceipt } from "@sharpana/session-api/types";
import { UI_INTERACTION_RENDERERS } from "./uiInteractionRegistry";
import { FeedbackGate } from "./FeedbackGate";
import { SessionHelpBar } from "./SessionHelpBar";
import { RecalibrationBanner } from "./RecalibrationBanner";
import { HintCard } from "./HintCard";
import { SelectionPopover } from "./SelectionPopover";
import { SessionBottomBar } from "./SessionBottomBar";
import { SessionReceipt } from "./SessionReceipt";

const rootStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const scrollWrapStyle = css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
`;

const laneStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const sentinelStyle = css`
  height: 1px;
  flex-shrink: 0;
`;

const fadeBaseStyle = css`
  position: absolute;
  left: 0;
  right: 0;
  height: 32px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s ease;
  z-index: 2;
`;

const topFadeStyle = css`
  top: 0;
  background: linear-gradient(to bottom, var(--color-bg), transparent);
`;

const bottomFadeStyle = css`
  bottom: 0;
  background: linear-gradient(to top, var(--color-bg), transparent);
`;

const fadeVisibleStyle = css`
  opacity: 1;
`;

const scrollCueStyle = css`
  position: absolute;
  left: 50%;
  bottom: 6px;
  transform: translateX(-50%);
  z-index: 2;
  pointer-events: none;
  font-size: 0.95em;
  line-height: 1;
  color: var(--color-text-muted);
  opacity: 0.85;
  animation: helpScrollCue 1.4s ease-in-out infinite;

  @keyframes helpScrollCue {
    0%, 100% { transform: translateX(-50%) translateY(0); }
    50% { transform: translateX(-50%) translateY(3px); }
  }
`;

interface WorkLaneProps {
  work: WorkLaneType;
  isStreaming: boolean;
  onSubmitInteraction: (interactionId: string, value: UiInteractionValue, displayText: string) => void;
  onRevealHint: () => void;
  onOpenAsk: () => void;
  /** Highlight a term in the lesson → open its definition. */
  onDiveDeeper: (text: string) => void;
  /** A server-declared work-lane control was pressed (the way on). */
  onBottomBarAction: (actionId: string) => void;
  /** End the sitting early.  Absent = this sitting has no early exit. */
  onWrapUp?: () => void;
  /** The latest priced close, for the ledger tick above the bottom bar. */
  receipt?: EmissionReceipt | null;
  /** Planet titles, so the tick names a world the way the atlas does. */
  planetTitles?: Record<string, string>;
}

export const WorkLane: React.FC<WorkLaneProps> = ({
  work,
  isStreaming,
  onSubmitInteraction,
  onRevealHint,
  onOpenAsk,
  onDiveDeeper,
  onBottomBarAction,
  onWrapUp,
  receipt,
  planetTitles,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const [scrolledDown, setScrolledDown] = useState(false);

  const activeId = work.activeInteractionId;
  const activeInteraction = activeId ? work.interactions[activeId] : null;

  // Keep the newest content in view as steps arrive.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeId]);

  // Scroll affordance: a bottom fade + cue when there's more below the
  // fold (so the help bar is never mistaken for the end of the view), and
  // a symmetric top fade once scrolled.  Overflow-below is read off an
  // IntersectionObserver sentinel; the top fade off scrollTop.
  useEffect(() => {
    const el = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!el) return;

    const onScroll = () => setScrolledDown(el.scrollTop > 8);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });

    let io: IntersectionObserver | null = null;
    if (sentinel && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => setMoreBelow(!entry.isIntersecting),
        { root: el, threshold: 0 },
      );
      io.observe(sentinel);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      io?.disconnect();
    };
  }, []);

  const Renderer = activeInteraction
    ? UI_INTERACTION_RENDERERS[activeInteraction.kind as keyof typeof UI_INTERACTION_RENDERERS]
    : null;

  // An authored activity owns its whole surface: it scrolls inside the
  // ActivityHost's contained viewport (never the lane), carries its own
  // authored hints and feedback beats, and must fill exactly the space
  // between the header and the ask-coach help bar.  The selection popover
  // still wraps it so highlight → term preview → dive-deeper works over
  // authored content.
  if (activeInteraction?.kind === "authored_activity" && Renderer) {
    return (
      <div className={rootStyle}>
        <SelectionPopover onDiveDeeper={onDiveDeeper} disabled={false}>
          <Suspense fallback={null}>
            <Renderer
              interaction={activeInteraction as UiInteractionSpec}
              disabled={isStreaming}
              hasPriorInteraction={false}
              onSubmit={() => {}}
            />
          </Suspense>
        </SelectionPopover>
        {/* The ledger tick, in the host's chrome band — never over the
            activity, where it would land on the reveal the learner is
            reading. */}
        <SessionReceipt receipt={receipt ?? null} planetTitles={planetTitles} />
        {/* The way on, drawn by the host once the served instrument is
            finished — the activity draws none of its own, and nothing
            advances the route until this is pressed. */}
        <SessionBottomBar bottomBar={work.bottomBar} onAction={onBottomBarAction} />
        <SessionHelpBar onOpenAsk={onOpenAsk} onWrapUp={onWrapUp} />
      </div>
    );
  }

  // Prior answered interaction ⇒ the leading field is feedback (handled by
  // the FeedbackGate) rather than first-turn framing.
  const hasPriorInteraction = work.interactionOrder.some((id) => {
    const it = work.interactions[id];
    return it && it.status === "answered";
  });

  // A regenerated step ("feels too easy / hard") is a FRESH exercise, not
  // feedback on a prior answer: skip the feedback gate and let the
  // renderer show its own (recalibrated) framing, with a chip above it.
  const recalibrated = activeInteraction?.recalibrated;
  const previousVersion =
    recalibrated && activeInteraction?.supersedesId
      ? work.interactions[activeInteraction.supersedesId]
      : undefined;
  const effectiveHasPrior = recalibrated ? false : hasPriorInteraction;

  const rendered = activeInteraction && Renderer && (
    <Suspense fallback={null}>
      <Renderer
        interaction={activeInteraction as UiInteractionSpec}
        disabled={isStreaming}
        hasPriorInteraction={effectiveHasPrior}
        onSubmit={(value, displayText) =>
          onSubmitInteraction(activeInteraction.interactionId, value, displayText)
        }
      />
    </Suspense>
  );

  // The shared feedback beat wraps every graded kind so post-answer
  // feedback shows identically.  ``manim_animation`` is a presentation
  // (no graded answer); a recalibrated step shows fresh framing instead.
  const useGate =
    !!activeInteraction &&
    activeInteraction.kind !== "manim_animation" &&
    !recalibrated;

  return (
    <div className={rootStyle}>
      <div className={scrollWrapStyle}>
        <div className={laneStyle} ref={scrollRef} data-testid="work-lane">
          <SelectionPopover onDiveDeeper={onDiveDeeper} disabled={isStreaming}>
            {recalibrated && (
              <RecalibrationBanner direction={recalibrated} previous={previousVersion} />
            )}

            {activeInteraction && Renderer ? (
              useGate ? (
                <FeedbackGate
                  interaction={activeInteraction as UiInteractionSpec}
                  hasPriorInteraction={effectiveHasPrior}
                  streaming={isStreaming}
                >
                  {rendered}
                </FeedbackGate>
              ) : (
                rendered
              )
            ) : null}

            {activeInteraction && (
              <HintCard
                hints={activeInteraction.hints ?? []}
                streaming={isStreaming}
                onReveal={onRevealHint}
                interactionId={activeInteraction.interactionId}
              />
            )}
          </SelectionPopover>

          <div ref={sentinelRef} className={sentinelStyle} aria-hidden="true" />
        </div>

        <div
          className={`${fadeBaseStyle} ${topFadeStyle} ${scrolledDown ? fadeVisibleStyle : ""}`}
          aria-hidden="true"
        />
        <div
          className={`${fadeBaseStyle} ${bottomFadeStyle} ${moreBelow ? fadeVisibleStyle : ""}`}
          aria-hidden="true"
          data-testid="work-scroll-more"
        />
        {moreBelow && (
          <span className={scrollCueStyle} aria-hidden="true">⌄</span>
        )}
      </div>

      <SessionHelpBar onOpenAsk={onOpenAsk} />
    </div>
  );
};

